/**
 * TrivenyOrganizations — the Triveny operator's directory of tenant sign-ups.
 *
 * The plain "Org page" only ever shows the operator's own org; new sign-ups live
 * in platform_shared.platform_tenants and had no listing. This surfaces them via
 * GET /api/platform/auth/organizations (operator-only on the backend — a regular
 * tenant gets 403). Shows who signed up, how (source), their admin, size, and
 * activity. Triveny-only.
 */

import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Building2, RefreshCw, Search, AlertCircle, ChevronRight } from 'lucide-react';
import { config } from '@/src/config';
import { tvTokens, TvEyebrow } from '@/src/platform/shared/components/triveny';
import { useTabbedChrome } from '@/components/weavein';

interface Org {
  id: string;
  slug: string;
  name: string;
  tenant_type: string;
  status: string;
  source: string | null;
  user_count: number;
  admin_email: string | null;
  created_at: string | null;
  last_login_at: string | null;
}

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem('auth_token') || '';
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
}

const SOURCE_LABEL: Record<string, string> = {
  self_service_signup: 'Self-serve',
  standalone_provisioning: 'Provisioned',
};

function rel(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso).getTime();
  const days = Math.floor((Date.now() - d) / 86400000);
  if (days < 1) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days}d ago`;
  if (days < 365) return `${Math.round(days / 30)}mo ago`;
  return `${Math.round(days / 365)}y ago`;
}
function dateStr(iso?: string | null): string {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }); } catch { return '—'; }
}

export default function TrivenyOrganizations() {
  const tabbed = useTabbedChrome();
  const base = config.weaveinApi?.baseUrl || '';
  const [orgs, setOrgs] = useState<Org[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);
  const [usersByOrg, setUsersByOrg] = useState<Record<string, any[] | 'loading'>>({});

  const toggleUsers = useCallback(async (org: Org) => {
    if (expanded === org.id) { setExpanded(null); return; }
    setExpanded(org.id);
    if (usersByOrg[org.id]) return;
    setUsersByOrg((m) => ({ ...m, [org.id]: 'loading' }));
    try {
      const res = await fetch(`${base}/api/platform/auth/organizations/${org.id}/users`, { headers: authHeaders() });
      const data = res.ok ? await res.json() : { users: [] };
      setUsersByOrg((m) => ({ ...m, [org.id]: Array.isArray(data?.users) ? data.users : [] }));
    } catch {
      setUsersByOrg((m) => ({ ...m, [org.id]: [] }));
    }
  }, [base, expanded, usersByOrg]);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await fetch(`${base}/api/platform/auth/organizations`, { headers: authHeaders() });
      if (res.status === 403) {
        setError('The organization directory is available to the platform operator only.');
        setOrgs([]);
        return;
      }
      if (!res.ok) throw new Error(`(${res.status})`);
      const data = await res.json();
      setOrgs(Array.isArray(data?.organizations) ? data.organizations : []);
    } catch (e: any) {
      setError(`Couldn't load organizations ${e?.message || ''}`.trim());
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [base]);

  useEffect(() => { void load(); }, [load]);

  const rows = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return orgs;
    return orgs.filter(
      (o) => o.name?.toLowerCase().includes(term) || o.slug?.toLowerCase().includes(term) || (o.admin_email || '').toLowerCase().includes(term),
    );
  }, [orgs, q]);

  const newThisMonth = useMemo(() => {
    const since = Date.now() - 30 * 86400000;
    return orgs.filter((o) => o.created_at && new Date(o.created_at).getTime() >= since).length;
  }, [orgs]);

  const th: React.CSSProperties = { textAlign: 'left', fontFamily: tvTokens.fontMono, fontSize: 10, letterSpacing: '0.06em', textTransform: 'uppercase', color: tvTokens.ink500, fontWeight: 600, padding: '10px 14px', background: tvTokens.paper2, borderBottom: `1px solid ${tvTokens.border}` };
  const td: React.CSSProperties = { padding: '11px 14px', borderTop: `1px solid ${tvTokens.borderMuted}`, color: tvTokens.ink700, fontSize: 13.5, verticalAlign: 'middle' };
  const pill = (bg: string, color: string): React.CSSProperties => ({ fontFamily: tvTokens.fontMono, fontSize: 10.5, fontWeight: 600, padding: '2px 8px', borderRadius: 20, background: bg, color, whiteSpace: 'nowrap' });

  return (
    <div style={{ maxWidth: 1080, margin: '0 auto', padding: '28px 22px 80px', fontFamily: tvTokens.fontSans, color: tvTokens.ink }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, marginBottom: 18, flexWrap: 'wrap' }}>
        <div>
          {!tabbed && (
            <>
              <TvEyebrow>Platform</TvEyebrow>
              <h1 style={{ fontFamily: tvTokens.fontDisplay, fontSize: 26, fontWeight: 650, letterSpacing: '-0.02em', margin: '6px 0 4px', display: 'flex', alignItems: 'center', gap: 10 }}>
                <Building2 size={24} color={tvTokens.accent} /> Organizations
              </h1>
            </>
          )}
          <p style={{ color: tvTokens.ink500, fontSize: 14, margin: 0 }}>
            {orgs.length} {orgs.length === 1 ? 'organization' : 'organizations'}
            {newThisMonth > 0 && <span style={{ color: tvTokens.accentInk }}> · {newThisMonth} new in 30 days</span>}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <div style={{ position: 'relative' }}>
            <Search size={15} color={tvTokens.ink400} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)' }} />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, slug, admin…" style={{ height: 36, padding: '0 12px 0 32px', width: 240, borderRadius: tvTokens.r, border: `1px solid ${tvTokens.border}`, background: tvTokens.paper, color: tvTokens.ink, fontSize: 13, outline: 'none' }} />
          </div>
          <button onClick={() => { setRefreshing(true); void load(); }} disabled={refreshing} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 12px', background: 'transparent', border: `1px solid ${tvTokens.border}`, borderRadius: tvTokens.r, color: tvTokens.ink700, fontSize: 12, fontWeight: 500, cursor: 'pointer' }}>
            <RefreshCw size={13} style={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} /> Refresh
          </button>
        </div>
      </div>

      {error ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 20, border: `1px solid ${tvTokens.border}`, borderRadius: 12, background: tvTokens.surface, color: tvTokens.ink500, fontSize: 14 }}>
          <AlertCircle size={16} /> {error}
        </div>
      ) : (
        <div style={{ border: `1px solid ${tvTokens.border}`, borderRadius: 12, overflow: 'hidden', background: tvTokens.surface }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 820 }}>
              <thead>
                <tr>{['Organization', 'Admin', 'Sign-up', 'Users', 'Status', 'Joined', 'Last active'].map((h) => <th key={h} style={th}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={7} style={{ ...td, textAlign: 'center', color: tvTokens.ink400 }}>Loading…</td></tr>
                ) : rows.length === 0 ? (
                  <tr><td colSpan={7} style={{ ...td, textAlign: 'center', color: tvTokens.ink400 }}>{q ? 'No matches.' : 'No organizations yet.'}</td></tr>
                ) : (
                  rows.map((o) => (
                    <Fragment key={o.id}>
                    <tr onClick={() => toggleUsers(o)} style={{ cursor: 'pointer', background: expanded === o.id ? tvTokens.paper2 : undefined }}>
                      <td style={td}>
                        <div style={{ fontWeight: 600, color: tvTokens.ink, display: 'flex', alignItems: 'center', gap: 6 }}>
                          <ChevronRight size={14} color={tvTokens.ink400} style={{ transform: expanded === o.id ? 'rotate(90deg)' : 'none', transition: 'transform .12s' }} />
                          {o.name}
                        </div>
                        <div style={{ fontFamily: tvTokens.fontMono, fontSize: 10.5, color: tvTokens.ink400, marginLeft: 20 }}>{o.slug}</div>
                      </td>
                      <td style={{ ...td, color: tvTokens.ink700 }}>{o.admin_email || '—'}</td>
                      <td style={td}>
                        {(o.source === 'self_service_signup' || o.source === 'standalone_provisioning') ? (
                          // Both are the public triveny.ai self-serve signup path.
                          <span style={pill('#e3f3ec', '#12855f')}>● Self-serve</span>
                        ) : (
                          <span style={pill(tvTokens.paper2 as string, tvTokens.ink500 as string)}>{o.source || o.tenant_type || '—'}</span>
                        )}
                      </td>
                      <td style={{ ...td, fontVariantNumeric: 'tabular-nums' }}>{o.user_count}</td>
                      <td style={td}><span style={o.status === 'active' ? pill('#e3f3ec', '#12855f') : pill(tvTokens.paper2 as string, tvTokens.ink500 as string)}>{o.status}</span></td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }} title={o.created_at || ''}>{dateStr(o.created_at)}</td>
                      <td style={{ ...td, color: tvTokens.ink500 }}>{rel(o.last_login_at)}</td>
                    </tr>
                    {expanded === o.id && (
                      <tr>
                        <td colSpan={7} style={{ padding: '0 14px 12px 34px', background: tvTokens.paper2, borderTop: 'none' }}>
                          {usersByOrg[o.id] === 'loading' ? (
                            <div style={{ padding: 12, color: tvTokens.ink400, fontSize: 13 }}>Loading users…</div>
                          ) : ((usersByOrg[o.id] as any[]) || []).length === 0 ? (
                            <div style={{ padding: 12, color: tvTokens.ink400, fontSize: 13 }}>No users.</div>
                          ) : (
                            <div style={{ border: `1px solid ${tvTokens.border}`, borderRadius: 8, overflow: 'hidden', background: tvTokens.surface, marginTop: 8 }}>
                              {((usersByOrg[o.id] as any[]) || []).map((u, i) => (
                                <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderTop: i === 0 ? 'none' : `1px solid ${tvTokens.borderMuted}`, fontSize: 13 }}>
                                  <span style={{ fontWeight: 600, color: tvTokens.ink, minWidth: 140 }}>{u.name}</span>
                                  <span style={{ color: tvTokens.ink700, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{u.email}</span>
                                  <span style={pill(tvTokens.paper2 as string, tvTokens.ink500 as string)}>{u.role}</span>
                                  {!u.email_verified && <span style={pill('#f8efda', '#b9781f')}>unverified</span>}
                                  <span style={{ fontSize: 11.5, color: tvTokens.ink400, minWidth: 90, textAlign: 'right' }}>{u.login_count > 0 ? `${rel(u.last_login_at)}` : 'never logged in'}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                    </Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
